import React, { type ChangeEvent, type FC, type ReactElement, useEffect, useState } from 'react'
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import LoraLogo from '../../../assets/lora.png'
import { createDefaultAlarmSettings, isAlarmEnabled } from '../../../utils/utils'
import { LabelsInput } from './LabelsInput'
import {
  SettingsGeneralButton,
  SettingsSaveButton,
  SettingsTabTableCell,
  StyledAppBar,
  StyledBaseReading,
  StyledBaseReadingTextField,
  StyledFieldsText,
  StyledLabelFullWidth,
  StyledSettingsTableCell,
  StyledTitle
} from './MeterConfiguration.style'
import clsx from 'clsx'
import { StyledInput } from '../meters-table/meters-table-row/MetersTableRow.style'
import { SetMeterInEditorAction, UpdateMeterAction } from '../../../redux/meters/meter-actions'
import { meterSupportsAlarm } from '../../../utils/meter-utils'
import { isPulseDevice, type Meter } from '../../../redux/meters/meter-types'
import { DeleteMeterButton } from './DeleteMeter'
import { useTranslation } from 'react-i18next'
import {
  type AlarmType,
  AlarmTypes,
  FLOW_LIMIT,
  getAlarmTypeByKey,
  getNameTranslationKey
} from '../../../redux/alarm-settings/alarm-types'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { type RootState } from '../../../redux/store'
import { ToggleMeterSettingsDialogAction } from '../../../redux/meter-table/meter-table-actions'
import { AddMessageAction } from '../../../redux/messages/messages-actions'
import { MessageTypes } from '../../../redux/messages/messages-types'

const useStyles = makeStyles()(() => {
  return {
    root: {
      flexGrow: 1
    },
    tabs: {
      '& .MuiButtonBase-root.MuiTab-root': {
        fontSize: 12,
        backgroundColor: '#95aac9',
        borderRadius: '5px 5px 0 0',
        margin: '16px 16px 0 16px'
      },
      '& .MuiTabs-indicator': {
        display: 'none'
      },
      '& .MuiTab-textColorInherit': {
        color: 'inherit',
        opacity: 0.7
      },
      '& .Mui-selected': {
        textDecoration: 'none',
        backgroundColor: '#ffffff !important',
        color: '#12263F'
      }
    },
    label: {
      '& .MuiFormControlLabel-label': {
        color: '#12263f',
        fontSize: 10
      }
    },
    icon: {
      borderRadius: 3,
      width: 16,
      height: 16,
      boxShadow:
                'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
      backgroundColor: '#f5f8fa',
      '$root.Mui-focusVisible &': {
        outline: '2px auto rgba(19,124,189,.6)',
        outlineOffset: 2,
        border: '1px solid #00D97D'
      }
    },
    checkedIcon: {
      backgroundColor: '#7CFFC8',
      '&:before': {
        display: 'block',
        width: 16,
        height: 16,
        content: '""'
      }
    }
  }
})

export const MeterConfiguration: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { classes } = useStyles()

  const meterInEditor: Meter | null = useAppSelector((state: RootState) => state.meter.meterInEditor)
  const meterSettingsDialogOpen = useAppSelector<boolean>((state: RootState) => state.meterTable.meterSettingsDialogOpen)

  const [currentTab, setCurrentTab] = useState(0)
  const showAlarmsTab: boolean = meterInEditor?.latestLoraStatus != null

  // Only load these once when the modal has opened
  useEffect(() => {
    if (meterInEditor?.id != null) {
      if (!showAlarmsTab) {
        setCurrentTab(1)
      }
    }
  }, [dispatch, meterInEditor, showAlarmsTab])

  if (meterInEditor === null) {
    return null
  }

  return (
        <Dialog open={meterSettingsDialogOpen} maxWidth="md" fullWidth>
            <StyledTitle>{t('settings_dialog.meter_settings')}</StyledTitle>
            <DialogContent>
                <MeterGeneralInfo meter={meterInEditor}/>
                <MeterBaseValue meter={meterInEditor}/>
                <MeterLabels meter={meterInEditor}/>

                {/* Tabs */}
                <div style={{ marginTop: '16px', border: '1px solid #edf2f9' }}>
                    <div className={classes.root}>
                        <StyledAppBar position="static">
                            <Tabs
                                className={classes.tabs}
                                value={currentTab}
                                onChange={(_, newTab: number) => {
                                  setCurrentTab(newTab)
                                }}
                                textColor="inherit"
                            >
                                {showAlarmsTab
                                  ? (
                                        <Tab
                                            disableRipple
                                            data-testid={'alarms-tab-button'}
                                            label={t('alarms.alarms')}
                                        />
                                    )
                                  : null}
                            </Tabs>
                        </StyledAppBar>
                    </div>
                    {showAlarmsTab
                      ? (
                            <TabPanel tabId={0} currentTabId={currentTab}>
                                <MeterAlarms meter={meterInEditor}/>
                            </TabPanel>
                        )
                      : null}
                </div>
                <Divider style={{ margin: '20px 0' }}/>
            </DialogContent>
            <DialogActions>
                <DeviceAlarmsDialogActions meter={meterInEditor}/>
            </DialogActions>
        </Dialog>
  )
}

/* Child-components */

interface DeviceAlarmsDialogActionsProps {
  meter: Meter
}

function DeviceAlarmsDialogActions({
  meter
}: Readonly<DeviceAlarmsDialogActionsProps>): ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const onSettingsButtonClick = (): void => {
    dispatch(SetMeterInEditorAction(null))
    dispatch(ToggleMeterSettingsDialogAction())
  }

  const onSaveButtonClick = (): void => {
    dispatch(
      UpdateMeterAction({
        meterId: meter.id,
        baseValue: meter.baseValue,
        serialNumber: meter.serialNumber,
        pulseInLiters: meter.pulseInLiters,
        transmitterSerialNumber: meter.transmitterSerialNumber,
        location: meter.location,
        alarmSettings: meter.alarmSettings ?? createDefaultAlarmSettings(),
        labels: meter.labels
      })
    )
  }

  return (
        <>
            <DeleteMeterButton/>
            <SettingsGeneralButton
                onClick={onSettingsButtonClick}
            >
                {t('settings_dialog.cancel')}
            </SettingsGeneralButton>
            <SettingsSaveButton
                onClick={onSaveButtonClick}
            >
                {t('settings_dialog.ok')}
            </SettingsSaveButton>
        </>
  )
}

interface AlarmSettingProps {
  alarmType: AlarmType
  rowNumber: number
  meter: Meter
}

function AlarmSetting({ alarmType, rowNumber, meter }: Readonly<AlarmSettingProps>): ReactElement | null {
  const { classes } = useStyles()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const existingAlarmSettings = meter.alarmSettings ?? createDefaultAlarmSettings()
  const checked = isAlarmEnabled(existingAlarmSettings, alarmType)
  const flowLimitAlarmType = alarmType.alarmKey === FLOW_LIMIT.alarmKey
  const isAlarmTypeSupported = meterSupportsAlarm(meter.type, alarmType)

  // Only updates/changes the wanted setting in state. This does not update AlarmSettings to the backend.
  function onAlarmSettingChange(
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): void {
    const settingKey = getAlarmTypeByKey(event.currentTarget.id).enabledKey

    if (settingKey === undefined || settingKey === null) {
      return
    }

    dispatch(
      SetMeterInEditorAction({
        ...meter,
        alarmSettings: {
          ...existingAlarmSettings,
          [settingKey]: checked
        }
      })
    )
  }

  function onFlowLimitChange(event: ChangeEvent<HTMLInputElement>): void {
    const rawValue = event.target.value.trim()
    const limit = rawValue === '' ? undefined : parseInt(rawValue, 10)

    if (limit != null && isNaN(limit)) {
      dispatch(AddMessageAction('errors.flow_limit_invalid', MessageTypes.error))
      return
    }

    dispatch(
      SetMeterInEditorAction({
        ...meter,
        alarmSettings: {
          ...existingAlarmSettings,
          maxFlowPerHour: limit
        }
      })
    )
  }

  const flowValue: string = flowLimitAlarmType && existingAlarmSettings.maxFlowPerHour != null
    ? existingAlarmSettings.maxFlowPerHour + ''
    : ''

  return (
        <TableRow style={{ opacity: !isAlarmTypeSupported ? '0.38' : '' }}>
            <SettingsTabTableCell>{rowNumber + 1}</SettingsTabTableCell>
            <SettingsTabTableCell>
                {t(getNameTranslationKey(alarmType))}
            </SettingsTabTableCell>
            <SettingsTabTableCell>
                {isAlarmTypeSupported
                  ? (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                className={classes.label}
                                label=""
                                control={
                                    <Checkbox
                                        checked={checked}
                                        onChange={onAlarmSettingChange}
                                        icon={<span className={classes.icon}/>}
                                        checkedIcon={
                                            <span
                                                className={clsx(
                                                  classes.icon,
                                                  classes.checkedIcon
                                                )}
                                            />
                                        }
                                        id={alarmType.alarmKey}
                                        data-testid={alarmType.enabledKey + '-checkbox'}
                                    />
                                }
                            />
                            {flowLimitAlarmType
                              ? (
                                    <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          marginLeft: '40px'
                                        }}
                                    >
                                        <StyledBaseReadingTextField
                                            value={flowValue}
                                            onChange={onFlowLimitChange}
                                            data-testid="flowLimitTextField"
                                        />
                                        <StyledBaseReading>l/h</StyledBaseReading>
                                    </div>
                                )
                              : null}
                        </div>
                    )
                  : (
                      t('settings_dialog.meter_alarm_unsupported')
                    )}
            </SettingsTabTableCell>
        </TableRow>
  )
}

function TabPanel({ children, tabId, currentTabId }: Readonly<{
  children: ReactElement | ReactElement[]
  tabId: number
  currentTabId: number
}>): ReactElement {
  return <div hidden={currentTabId !== tabId}>{children}</div>
}

const MeterLogo = (): ReactElement => (
    <img src={LoraLogo} alt="lora-logo" style={{ width: '35px' }}/>
)

function MeterGeneralInfo({ meter }: Readonly<{ meter: Meter }>): ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  function onLocationChange(e: ChangeEvent<HTMLInputElement>): void {
    dispatch(
      SetMeterInEditorAction({
        ...meter,
        location: e.target.value
      })
    )
  }

  function onSerialNumberChange(e: ChangeEvent<HTMLInputElement>): void {
    dispatch(
      SetMeterInEditorAction({
        ...meter,
        serialNumber: e.target.value
      })
    )
  }

  function onTransmitterSerialNumberChange(e: ChangeEvent<HTMLInputElement>): void {
    dispatch(
      SetMeterInEditorAction({
        ...meter,
        transmitterSerialNumber: e.target.value
      })
    )
  }

  const isPulseMeter = isPulseDevice(meter.type)

  return (
        <Table>
            <TableHead>
                <TableRow>
                    <StyledSettingsTableCell/>
                    <StyledSettingsTableCell>{t('radio_id')}</StyledSettingsTableCell>
                    <StyledSettingsTableCell>
                        {t('usage_location_number')}
                    </StyledSettingsTableCell>
                    <StyledSettingsTableCell>
                        {t('serial_number')}
                    </StyledSettingsTableCell>
                    <StyledSettingsTableCell>
                        {t('transmitter_serial_number')}
                    </StyledSettingsTableCell>
                    <StyledSettingsTableCell>
                        {t('pulse_liter_per_impulse')}
                    </StyledSettingsTableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell style={{ display: 'flex', alignItems: 'center' }}>
                        <MeterLogo/>
                        {isPulseMeter && (
                            <span style={{ color: '#0080FF' }}>&nbsp;Pulse</span>
                        )}
                    </TableCell>
                    <TableCell>{meter.devEui}</TableCell>
                    <TableCell>
                        <StyledInput
                            id="meter_location"
                            value={meter.location}
                            onChange={onLocationChange}
                            isIdInput={false}
                            inputProps={{ 'data-testid': 'meterLocationChangeInput' }}
                        />
                    </TableCell>
                    <TableCell>
                        <StyledInput
                            id="serial_number"
                            value={meter.serialNumber}
                            onChange={onSerialNumberChange}
                            isIdInput={false}
                            inputProps={{ 'data-testid': 'serialNumberChangeInput' }}
                        />
                    </TableCell>
                    <TableCell>
                        <StyledInput
                            id="transmitter_serial_number"
                            value={meter.transmitterSerialNumber}
                            onChange={onTransmitterSerialNumberChange}
                            isIdInput={false}
                            inputProps={{ 'data-testid': 'transmitterSerialNumberChangeInput' }}
                        />
                    </TableCell>
                    <TableCell>
                        {isPulseMeter ? meter.pulseInLiters : '--'}
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
  )
}

function MeterBaseValue({ meter }: Readonly<{ meter: Meter }>): ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const baseValue = (meter.baseValue != null) ? meter.baseValue / 1000 : undefined

  function onChange(e: ChangeEvent<HTMLInputElement>): void {
    const newBaseValue = e.currentTarget.value
    const parsed = parseInt((Math.floor(+newBaseValue * 100) * 10).toString(), 10)

    if (isNaN(parsed)) {
      dispatch(AddMessageAction('base_value_invalid', MessageTypes.error))
    } else {
      dispatch(
        SetMeterInEditorAction({
          ...meter,
          baseValue: parsed
        })
      )
    }
  }

  return (
        <div style={{ padding: '10px 0' }}>
            <StyledFieldsText data-tip={t('settings_dialog.base_reading_tooltip')}>
                {t('settings_dialog.base_reading_info_text')}
            </StyledFieldsText>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <StyledBaseReadingTextField
                    id="MeterBaseValueTextField"
                    data-testid="MeterBaseValueTextField"
                    value={baseValue}
                    onChange={onChange}
                />
                <StyledBaseReading>{`m${String.fromCharCode(179)}`}</StyledBaseReading>
            </div>
        </div>
  )
}

function MeterLabels({ meter }: Readonly<{ meter: Meter }>): ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [labelValue, setLabelValue] = useState('')
  const [labelList, setLabelList] = useState<string[]>([])

  useEffect(() => {
    setLabelList(meter.labels)
  }, [meter])

  function onLabelValueChange(newValue: string): void {
    setLabelValue(newValue)
  }

  function onInsertLabel(value: string): void {
    if (value === '') {
      dispatch(AddMessageAction('labels.emptyLabelError', MessageTypes.error))
    } else if (labelList.includes(value)) {
      dispatch(AddMessageAction('labels.labelAlreadyExistsError', MessageTypes.error))
    } else {
      const newLabels = value
        .split(',')
        .map(l => l.trim())
        .filter(l => l !== '')

      dispatch(
        SetMeterInEditorAction({
          ...meter,
          labels: [...meter.labels, ...newLabels]
        })
      )
    }

    setLabelValue('')
  }

  function handleLabelDelete(labelToDelete: string): void {
    const filteredLabels = meter.labels.filter(
      (label) => label !== labelToDelete
    )
    dispatch(
      SetMeterInEditorAction({
        ...meter,
        labels: filteredLabels
      })
    )
  }

  return (
        <div style={{ padding: '10px 0' }}>
            <StyledFieldsText>{t('labels.labels')}</StyledFieldsText>
            <LabelsInput
                onLabelValueChange={onLabelValueChange}
                onInsertNewLabel={onInsertLabel}
                labelValue={labelValue}
            />
            <div style={{ margin: '16px 0' }}>
                {labelList.map((item: string) => {
                  return (<StyledLabelFullWidth
                        key={item}
                        label={item}
                        onDelete={() => {
                          handleLabelDelete(item)
                        }}
                    />)
                })}
            </div>
        </div>
  )
}

function MeterAlarms({ meter }: Readonly<{ meter: Meter }>): ReactElement {
  const { t } = useTranslation()
  return (
        <Table>
            <TableHead>
                <TableRow>
                    <StyledSettingsTableCell>{'#'}</StyledSettingsTableCell>
                    <StyledSettingsTableCell>{t('alarm')}</StyledSettingsTableCell>
                    <StyledSettingsTableCell>{t('state')}</StyledSettingsTableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {(Object.values(AlarmTypes)).map((alarmType, index) => (
                    <AlarmSetting
                        key={alarmType.alarmKey}
                        alarmType={alarmType}
                        rowNumber={index}
                        meter={meter}
                    />
                ))
                }
            </TableBody>
        </Table>
  )
}
